import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { prettyFloatMoney } from '@/helpers/utils';
import { useApplicationStatusByFundraisingId } from '@/hooks/shared/useApplicationStatusByFundraisingId';
import { useSubmitFundraisingApplication } from '@/query/queries/fundraising/useSubmitFundraisingApplication';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { APP_LINKS } from '@/helpers/links';
import { MODAL_IDS } from '@/kit/Modal/keys';
import IconCheck from './icon_check.svg';
import IconCoins from './icon_coins.svg';
import IconRefresh from './icon_refresh.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.confirm_application_modal;

export const ConfirmApplicationModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({ newFunds, refinancing: selectedRefin, fundraisingId }) => {
  const { closeModal } = useModal();
  const navigate = useNavigate();
  const { application, applicationWithoutDocsRequired } = useApplicationStatusByFundraisingId(fundraisingId, {
    newFunds,
    selectedRefin
  });
  const { mutateAsync: submitApplication } = useSubmitFundraisingApplication(
    application?.application_id,
    fundraisingId,
  );
  const { t } = useTranslation('modal');

  const onApplicationSubmit = async (new_funds, refins) => {
    const payload = {
      new_funds,
      refinancings: refins?.map(f => f.fundraising_id),
      documents: null,
    };
    await submitApplication(payload);
  };

  if (!application) return null;

  const refinancing = application?.refinancings.filter(r =>
    selectedRefin.includes(r.fundraising_id),
  );

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.icon}>
          <img src={IconCheck} alt="check" width={56} height={56} />
        </div>
        <div className="f-hero-h5">{t('confirm_app_modal.confirm_application')}</div>
        <div className="f-small-regular">
          {t('confirm_app_modal.round', { name: application.fundraising_name })}
        </div>
      </div>
      <div className={s.content}>
        <div className={classnames('f-body-accent', s.subtitle)}>
          {t('confirm_app_modal.goal_structure')}
        </div>
        <div className={s.row}>
          <div className={s.left}>
            <img src={IconCoins} alt="coins" width={20} height={20} />
            <span>{t('confirm_app_modal.new_funds')}</span>
          </div>
          <div className={s.right}>{prettyFloatMoney(newFunds)}</div>
        </div>
        {refinancing.map(refin => (
          <div className={s.row} key={refin.title}>
            <div className={s.left}>
              <img src={IconRefresh} alt="refresh" width={20} height={20} />
              <span>{refin.title}</span>
            </div>
            <div className={s.right}>{prettyFloatMoney(refin.amount)}</div>
          </div>
        ))}
      </div>
      <div className={s.footer}>
        <button
          type="button"
          className={classnames(r.grey_large, s.btn_save)}
          onClick={() => closeModal(id)}
        >
          {t('confirm_app_modal.cancel')}
        </button>
        <button
          type="button"
          className={classnames(r.black_large, s.btn_save)}
          onClick={async () => {
            if (application) {
              applicationWithoutDocsRequired
                ? await onApplicationSubmit(newFunds, refinancing)
                : navigate(APP_LINKS.fundraisingApplication, {
                    state: {
                      newFunds,
                      refinancing,
                      applicationId: application?.application_id,
                      fundraisingId,
                    },
                  });
            }
            closeModal(id);
          }}
        >
          {t('confirm_app_modal.confirm')}
        </button>
      </div>
    </div>
  );

  return (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
